import AssetImage from "@/components/AssetImage";
import {ModalBody, ModalFooter} from "@/widgets/ModalParts";
import {Button} from "@/components/buttons/Button";
import Checkbox from "@/components/inputs/Checkbox";
import {Modal} from "@/widgets/Modal";
import { useGetAsset } from "@/hooks/useAsset";
import { useAcceptChallenge } from "@/hooks/useAcceptChallenge";
import { addError } from "@/redux/meta/metaActions";
import { Challenge, ChallengeInfoResponse } from "@devour/client";
import { FC, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { useDispatch } from "react-redux";

interface props {
    challenge: Challenge | ChallengeInfoResponse;
    isVisible: boolean;
    onAccepted?: (newsletterOptIn?: boolean, zipCode?: string) => void;
    onClose: () => void;
    /** If true, shows newsletter opt-in and zip code fields. Default false for quest-level modals */
    showNewsletterFields?: boolean;
    /** Callback to open rules modal (for Polaris challenge) */
    onOpenRules?: () => void;
    /** Override brand name (used when challenge.brandName is empty, e.g., from Hub) */
    brandNameOverride?: string;
    /** Override brand color (used when challenge.brandColor is empty, e.g., from Hub) */
    brandColorOverride?: string;
}

// Extended type for fields not yet in generated client
interface ExtendedChallenge {
    termsContent?: string;
}

export const ChallengeAcceptModal: FC<props> = ({ challenge, isVisible, onClose, onAccepted, showNewsletterFields = false, onOpenRules, brandNameOverride, brandColorOverride }) => {
    const [termsChecked, setTermsChecked] = useState<boolean>(false);
    const [newsletterOptIn, setNewsletterOptIn] = useState(true); // Checked by default
    const [zipCode, setZipCode] = useState("");
    const [isZipFocused, setIsZipFocused] = useState(false);
    const [isZipHovered, setIsZipHovered] = useState(false);
    const { data: background } = useGetAsset(challenge.backgroundImageLarge);
    const dispatch = useDispatch();
    const [isAccepting, setIsAccepting] = useState(false);
    const acceptChallengeMutation = useAcceptChallenge();

    // Use override values if challenge doesn't have them set
    const effectiveBrandColor = challenge.brandColor || brandColorOverride;
    const effectiveBrandName = challenge.brandName || brandNameOverride || challenge.name || "this brand";

    const isValidZipCode = (zip: string) => (/^\d{5}$/).test(zip);
    const canAccept = showNewsletterFields
        ? termsChecked && (!newsletterOptIn || isValidZipCode(zipCode))
        : termsChecked;
    const acceptChallenge = async () => {
        if (!canAccept) return;
        setIsAccepting(true);
        try {
            await acceptChallengeMutation.mutateAsync(challenge.id);
            if (onAccepted) {
                onAccepted(newsletterOptIn, newsletterOptIn ? zipCode : undefined);
            }
            onClose();
        } catch (err) {
            dispatch(await addError(err));
        }
        setIsAccepting(false);
    };
    return (
        <Modal size="xs1" isOpen={isVisible} toggle={onClose} contentClassName="brand-challenge_accept-modal" style={effectiveBrandColor ? { "--brand-color": effectiveBrandColor } as React.CSSProperties : undefined}>
            <div
                className="brand-challenge_accept-modal_background"
                style={{
                    backgroundImage: `url(${background?.cdnUrl})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}></div>
            <div
                className="brand-challenge_accept-modal_shader"
                style={{
                    background: "linear-gradient(180deg, rgba(45, 45, 47, 0.10) 0%, #2D2D2F 31.33%)",
                }}
            ></div>
            <IoMdClose className="brand-challenge_accept-modal_close" onClick={onClose}/>
            <ModalBody className="brand-challenge_accept-modal_body">
                <div className="brand-challenge_accept-modal_body_heading">
                    <AssetImage assetId={challenge.brandLogo} />
                </div>
                <div className="brand-challenge_accept-modal_body_content">
                    <h6 style={effectiveBrandColor ? {
                        textShadow: `0px 0px 24px ${effectiveBrandColor}, 0px 0px 8px ${effectiveBrandColor}`,
                    } : undefined}>Ready to Play?</h6>
                    <p>Accept and start completing challenges for prize entries.</p>
                </div>
                <Checkbox
                    onToggle={() => setTermsChecked(!termsChecked)}
                    checked={termsChecked}
                    brandColor={effectiveBrandColor}
                    className="brand-challenge_accept-modal_body_terms"
                >
                    Accept the {(challenge as any).slug === "polaris-challenge-2025"
                        ? <span
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onOpenRules) {
                                    onOpenRules();
                                }
                            }}
                            style={{cursor: "pointer", textDecoration: "underline", color: "inherit"}}
                        >Official Rules</span>
                        : <a
                            href={
                                (challenge as ExtendedChallenge).termsContent
                                    ? `/challenges/${challenge.id}/terms-rules`
                                    : challenge.termsUrl || "https://devourgo.io/terms-of-use"
                            }
                            target="_blank"
                            rel="noreferrer"
                        >challenge Terms & Conditions</a>
                    } of the {challenge.name}
                </Checkbox>
                {showNewsletterFields &&
                    <>
                        <Checkbox
                            onToggle={() => setNewsletterOptIn(!newsletterOptIn)}
                            checked={newsletterOptIn}
                            brandColor={effectiveBrandColor}
                            className="brand-challenge_accept-modal_body_terms"
                        >
                            Yes, please send me promotional emails from {effectiveBrandName} regarding future giveaways, product announcements, and special offers. You can opt out any time.
                        </Checkbox>
                        {newsletterOptIn &&
                            <div className="brand-challenge_accept-modal_body_zip-wrapper">
                                <input
                                    type="text"
                                    placeholder="Enter your zip code"
                                    value={zipCode}
                                    onChange={(e) => setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                                    className="brand-challenge_accept-modal_body_zip-input"
                                    maxLength={5}
                                    onFocus={() => setIsZipFocused(true)}
                                    onBlur={() => setIsZipFocused(false)}
                                    onMouseEnter={() => setIsZipHovered(true)}
                                    onMouseLeave={() => setIsZipHovered(false)}
                                    style={(isZipFocused || isZipHovered) && effectiveBrandColor ? {
                                        borderColor: effectiveBrandColor,
                                        outline: "none",
                                    } : undefined}
                                />
                                {zipCode.length > 0 && !isValidZipCode(zipCode) &&
                                    <span className="brand-challenge_accept-modal_body_zip-error">Please enter a valid 5-digit zip code</span>
                                }
                            </div>
                        }
                    </>
                }
            </ModalBody>
            <ModalFooter className="brand-challenge_accept-modal_footer">
                <Button variant="primary"
                    loading={isAccepting}
                    onClick={canAccept ? acceptChallenge : undefined}
                    {...effectiveBrandColor ? {
                        style: { backgroundColor: effectiveBrandColor, opacity: canAccept ? 1 : 0.5},
                    } : { style: { opacity: canAccept ? 1 : 0.5 } }}
                >
                    Accept challenge
                </Button>
                <Button variant="ghost" onClick={isAccepting ? undefined : onClose}>
                    Not now
                </Button>
            </ModalFooter>
        </Modal>
    );
};
